import { RecorderConfig, UploadResult, ProgressEvent, MultipartUploadInfo } from '../types';

export class UploadManager {
  private config: RecorderConfig;
  private activeUploads: Map<string, AbortController> = new Map();
  private progressCallbacks: Set<(progress: ProgressEvent) => void> = new Set();

  constructor(config: RecorderConfig) {
    this.config = config;
  }

  async uploadRecording(
      file: Blob,
      metadata: Record<string, unknown> = {}
  ): Promise<UploadResult> {
    const trackingId = this.generateTrackingId();
    const abortController = new AbortController();
    this.activeUploads.set(trackingId, abortController);

    try {
      // Get upload token from backend
      const uploadInfo = await this.getUploadToken(file, metadata);

      // Determine upload strategy based on file size
      const chunkSize = this.config.chunkSize ?? 5 * 1024 * 1024; // 5MB default

      if (file.size > chunkSize) {
        return await this.multipartUpload(file, uploadInfo as UploadResult & { multipart?: MultipartUploadInfo }, trackingId, abortController.signal);
      } else {
        return await this.singleUpload(file, uploadInfo, trackingId, abortController.signal);
      }
    } catch (error) {
      this.activeUploads.delete(trackingId);
      throw error;
    }
  }

  private async getUploadToken(file: Blob, metadata: Record<string, unknown>): Promise<UploadResult> {
    const response = await fetch(`${this.config.apiEndpoint}/upload-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.authHeaders ?? {})
      },
      body: JSON.stringify({
        filename: `recording-${Date.now()}.webm`,
        size: file.size,
        mimeType: file.type,
        metadata
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get upload token: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as UploadResult;
  }

  private async singleUpload(
      file: Blob,
      uploadInfo: UploadResult,
      trackingId: string,
      signal: AbortSignal
  ): Promise<UploadResult> {
    const xhr = new XMLHttpRequest();

    // initial progress
    this.emitProgress({
      trackingId,
      type: 'upload',
      progress: 0,
      bytesUploaded: 0,
      totalBytes: file.size
    });

    return new Promise((resolve, reject) => {
      const onAbort = () => {
        xhr.abort();
        reject(new Error('Upload cancelled'));
      };

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          this.emitProgress({
            trackingId,
            type: 'upload',
            progress,
            bytesUploaded: event.loaded,
            totalBytes: event.total
          });
        }
      };

      xhr.onload = () => {
        // S3/Cloud providers often return 200 or 204 for PUT
        if (xhr.status >= 200 && xhr.status < 300) {
          this.activeUploads.delete(trackingId);
          // finalize to 100% (in case the last progress didn't fire)
          this.emitProgress({
            trackingId,
            type: 'upload',
            progress: 100,
            bytesUploaded: file.size,
            totalBytes: file.size
          });
          resolve({ ...uploadInfo, trackingId });
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Upload failed due to network error'));
      };

      signal.addEventListener('abort', onAbort, { once: true });

      try {
        xhr.open('PUT', (uploadInfo as { uploadUrl: string }).uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      } catch (e) {
        reject(e);
      }
    });
  }

  private async multipartUpload(
      file: Blob,
      uploadInfo: UploadResult & { multipart?: MultipartUploadInfo },
      trackingId: string,
      signal: AbortSignal
  ): Promise<UploadResult> {
    if (!uploadInfo.multipart) {
      throw new Error('Multipart upload info not provided');
    }

    const { chunks } = uploadInfo.multipart;
    const chunkSize = this.config.chunkSize ?? 5 * 1024 * 1024;
    const parallelUploads = this.config.parallelUploads ?? 3;

    let uploadedBytes = 0;
    const completedChunks: { partNumber: number; etag: string }[] = [];

    // initial progress
    this.emitProgress({
      trackingId,
      type: 'upload',
      progress: 0,
      bytesUploaded: 0,
      totalBytes: file.size
    });

    // Upload chunks in parallel batches
    for (let i = 0; i < chunks.length; i += parallelUploads) {
      const batch = chunks.slice(i, i + parallelUploads);

      const batchPromises = batch.map(async (chunkInfo) => {
        const start = chunkInfo.chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const response = await fetch(chunkInfo.uploadUrl, {
          method: 'PUT',
          body: chunk,
          signal
        });

        if (!response.ok) {
          throw new Error(`Chunk upload failed: ${response.status} ${response.statusText}`);
        }

        const etag = response.headers.get('ETag');
        if (!etag) {
          throw new Error('Missing ETag in chunk upload response');
        }

        uploadedBytes += chunk.size;

        this.emitProgress({
          trackingId,
          type: 'upload',
          progress: (uploadedBytes / file.size) * 100,
          bytesUploaded: uploadedBytes,
          totalBytes: file.size
        });

        return {
          partNumber: chunkInfo.chunkIndex + 1,
          etag: etag.replace(/"/g, '')
        };
      });

      const batchResults = await Promise.all(batchPromises);
      completedChunks.push(...batchResults);
    }

    // Complete multipart upload
    await this.completeMultipartUpload(uploadInfo.multipart, completedChunks);

    // finalize to 100%
    this.emitProgress({
      trackingId,
      type: 'upload',
      progress: 100,
      bytesUploaded: file.size,
      totalBytes: file.size
    });

    this.activeUploads.delete(trackingId);
    return { ...uploadInfo, trackingId };
  }

  private async completeMultipartUpload(
      multipartInfo: MultipartUploadInfo,
      parts: { partNumber: number; etag: string }[]
  ): Promise<void> {
    const response = await fetch(`${this.config.apiEndpoint}/complete-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.config.authHeaders ?? {})
      },
      body: JSON.stringify({
        uploadId: multipartInfo.uploadId,
        key: multipartInfo.key,
        parts: parts.sort((a, b) => a.partNumber - b.partNumber)
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to complete upload: ${response.status} ${response.statusText}`);
    }
  }

  async retryUpload(trackingId: string): Promise<UploadResult> {
    // Implementation would retrieve failed upload info and retry
    throw new Error(`${trackingId} - Retry upload not yet implemented`);
  }

  async cancelUpload(trackingId: string): Promise<void> {
    const controller = this.activeUploads.get(trackingId);
    if (controller) {
      controller.abort();
      this.activeUploads.delete(trackingId);
    }
  }

  onProgress(callback: (progress: ProgressEvent) => void): void {
    this.progressCallbacks.add(callback);
  }

  offProgress(callback: (progress: ProgressEvent) => void): void {
    this.progressCallbacks.delete(callback);
  }

  private emitProgress(progress: ProgressEvent): void {
    this.progressCallbacks.forEach((callback) => {
      try {
        callback(progress);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }

  private generateTrackingId(): string {
    // e.g. rec_1700000000000_k3jx9q7c1
    return `rec_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  cleanup(): void {
    // Cancel all active uploads
    this.activeUploads.forEach((controller) => controller.abort());
    this.activeUploads.clear();
    this.progressCallbacks.clear();
  }
}
